import { useUIStore, type AccentId, type ThemePreference } from '../stores/uiStore';

const THEME_COLORS: Record<'light' | 'dark', string> = {
  light: '#fdf8f3',
  dark: '#1d1712',
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Resolve a stored preference to the concrete theme to render. */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'system') {
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
  }
  return preference;
}

/**
 * Apply theme + accent to <html>. The attribute always carries the
 * RESOLVED theme so portaled UI (Modal renders into document.body) and
 * native controls (via CSS color-scheme) follow it, and so the CSS never
 * needs prefers-color-scheme media blocks.
 */
export function applyTheme(preference: ThemePreference, accent: AccentId): void {
  const resolved = resolveTheme(preference);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.accent = accent;

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = THEME_COLORS[resolved];
  }
}

/**
 * Apply the persisted theme and keep <html> in sync with store changes
 * and OS appearance changes (when the preference is 'system').
 * Call once from main.tsx before render.
 */
export function initTheme(): void {
  const { theme, accent } = useUIStore.getState();
  applyTheme(theme, accent);

  useUIStore.subscribe(state => {
    applyTheme(state.theme, state.accent);
  });

  window.matchMedia(DARK_QUERY).addEventListener('change', () => {
    const state = useUIStore.getState();
    if (state.theme === 'system') {
      applyTheme(state.theme, state.accent);
    }
  });
}
